import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useTenantContext } from 'src/context/useTenantContext';
import { useUrlSearchFilter } from 'src/hooks/useUrlSearchFilter';
import { tenantInventoryMovementCreate, tenantInventoryMovements } from 'src/services/apis/tenantRequests';
import { InventoryMovementCreateDto } from 'src/services/apis/types';
import { buildCrudSearch } from 'src/utils/crudSearch';
import { QUERIES } from 'src/utils/react-query';

export const useInventoryMovements = () => {
  const { orgSlug, restaurantId, branchId } = useTenantContext();
  const urlSearch = useUrlSearchFilter();
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<{
    limit: number;
    type: number | null;
    search: string | null;
    itemId: string | null;
    startDate: Date | null;
    endDate: Date | null;
  }>({
    limit: 20,
    type: null,
    search: urlSearch,
    itemId: null,
    startDate: null,
    endDate: null,
  });
  const query = useQuery(
    [QUERIES.INVENTORY_MOVEMENTS, orgSlug, restaurantId, branchId, page, filters],
    ({ queryKey: [_key, _orgSlug, _restaurantId, _branchId, _page, _filters] }: { queryKey: any }) => {
      const convertedFilters: string[] = [];
      if (_filters.type) {
        convertedFilters.push(`type||$eq||${_filters.type}`);
      }
      if (_filters.itemId) {
        convertedFilters.push(`itemId||$eq||${_filters.itemId}`);
      }
      if (_filters.startDate && _filters.endDate) {
        convertedFilters.push(`date||$gte||${dayjs(_filters.startDate).startOf('day')}`);
        convertedFilters.push(`date||$lte||${dayjs(_filters.endDate).endOf('day')}`);
      }
      return tenantInventoryMovements(
        { orgSlug: _orgSlug as string, restaurantId: _restaurantId as string, branchId: _branchId as string },
        {
          page: _page as number,
          limit: _filters.limit,
          filter: convertedFilters,
          s: buildCrudSearch(_filters.search, ['note']),
        }
      );
    },
    { cacheTime: 0, refetchOnMount: true, keepPreviousData: true, refetchOnWindowFocus: false }
  );
  return { query, page, filters, setPage, setFilters };
};

export const useCreateInventoryMovementMutation = () => {
  const queryClient = useQueryClient();
  const { orgSlug, restaurantId, branchId } = useTenantContext();
  return useMutation(
    (payload: InventoryMovementCreateDto) =>
      tenantInventoryMovementCreate({ orgSlug, restaurantId, branchId }, payload),
    {
      onSuccess: () => {
        queryClient.refetchQueries([QUERIES.INVENTORY_MOVEMENTS, orgSlug, restaurantId, branchId]);
        queryClient.refetchQueries([QUERIES.INVENTORY_STOCK_LEVELS, orgSlug, restaurantId, branchId]);
        return queryClient.refetchQueries([QUERIES.DASHBOARD_INVENTORY_REPORTS, orgSlug, restaurantId, branchId]);
      },
    }
  );
};
